import type { IntlShape } from 'react-intl'
import { z } from 'zod'

import { parseCursorPage } from '@/shared/api/paging'
import { auditShape } from '@/shared/details'

import { classApi, classPath, classRoute } from '../class-form/class'
import { learnerClassApi } from '../learner/learner'

/** Manage Files, reached from Class Details' side menu, and its pages to add or edit a file. */
export const manageFilesRoute = `${classRoute}/files`
export const addFileRoute = `${manageFilesRoute}/new`
export const editFileRoute = `${manageFilesRoute}/:fileId/edit`

export function manageFilesPath(classId: string): string {
  return `${classPath(classId)}/files`
}

export function addFilePath(classId: string): string {
  return `${manageFilesPath(classId)}/new`
}

export function editFilePath(classId: string, fileId: string): string {
  return `${manageFilesPath(classId)}/${encodeURIComponent(fileId)}/edit`
}

/** The API's files on a class: GET lists them, POST adds one. */
export function classFilesApi(classId: string): string {
  return `${classApi(classId)}/files`
}

/** The API's file: GET reads it, PUT changes it, DELETE removes it. */
export function classFileApi(classId: string, fileId: string): string {
  return `${classFilesApi(classId)}/${encodeURIComponent(fileId)}`
}

/** The files on a class's learner page: student files for an Enrolled student, instructor files for its instructors. */
export function learnerClassFilesApi(classId: string): string {
  return `${learnerClassApi(classId)}/files`
}

/** What Manage Files needs: read to see and download files, write to add and change them, delete to remove them. */
export const readClassFilesPermission = 'classes.class-files.read'
export const writeClassFilesPermission = 'classes.class-files.write'
export const deleteClassFilesPermission = 'classes.class-files.delete'

/** The longest name and description, as the API takes them. */
export const nameLength = 150
export const descriptionLength = 800

const fileSchema = z
  .object({
    id: z.string(),
    classId: z.string(),
    name: z.string(),
    description: z.string().nullish(),
    isVisibleToInstructors: z.boolean(),
    isVisibleToStudents: z.boolean(),
    fileName: z.string(),
    fileSize: z.number(),
  })
  .extend(auditShape)

/** A file on a class. */
export type ClassFile = z.infer<typeof fileSchema>

export function parseClassFile(body: unknown): ClassFile {
  return fileSchema.parse(body)
}

/** A class's files, by name. */
export function parseClassFiles(body: unknown): readonly ClassFile[] {
  return parseCursorPage(body, parseClassFile).data
}

const contentSchema = z.object({ url: z.string().min(1) })

/** The short-lived link that downloads a file now. */
export function parseClassFileContent(body: unknown): string {
  return contentSchema.parse(body).url
}

/** A file's size in kilobytes or megabytes, as the person's locale writes them. */
export function fileSizeText(intl: IntlShape, bytes: number): string {
  const megabytes = bytes / (1024 * 1024)
  return megabytes >= 1
    ? intl.formatNumber(megabytes, { style: 'unit', unit: 'megabyte', maximumFractionDigits: 1 })
    : intl.formatNumber(Math.max(bytes / 1024, 0.1), { style: 'unit', unit: 'kilobyte', maximumFractionDigits: 1 })
}

/** Opens a file's download link in a new tab, opened before the link is asked for so the browser does not block it. */
export async function openDownload(readLink: () => Promise<unknown>): Promise<void> {
  const opened = window.open('', '_blank')
  try {
    const url = parseClassFileContent(await readLink())
    if (opened === null) {
      window.location.assign(url)
    } else {
      opened.opener = null
      opened.location.href = url
    }
  } catch (error: unknown) {
    opened?.close()
    throw error
  }
}
